// Welcome page — where the invite link lands. Validates the token and points
// the new joiner at the login page; the first-login gate then routes them
// into the onboarding wizard. No auto-login any more.
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../api/api";
import emLogo from "../../images/em-logo.svg";
import "./onboarding.css";

export default function OnboardingWelcome() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/onboarding/${token}`);
        if (!cancelled) setState({ status: "valid", email: res.data?.email });
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.data?.code;
        if (code === "ALREADY_ONBOARDED") setState({ status: "used" });
        else if (code === "EXPIRED_TOKEN") setState({ status: "expired" });
        else setState({ status: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Brand mark above every state, matching the accept-invite card. The icon
  // row is conditional: the welcome state passes "" and an empty div would
  // leave its 14px margin as a gap under the logo.
  const card = (icon, title, body, cta) => (
    <div className="ob-welcome-bg">
      <div className="ob-welcome-card">
        <img className="ob-welcome-logo" src={emLogo} alt="EM" />
        {icon && (
          <div className="ob-welcome-icon" aria-hidden="true">{icon}</div>
        )}
        <h1 className="ob-welcome-title">{title}</h1>
        <p className="ob-welcome-body">{body}</p>
        {cta}
      </div>
    </div>
  );

  if (state.status === "loading")
    return card("⏳", "One moment…", "Checking your invitation link.");

  if (state.status === "valid")
    return card(
      "",
      "Welcome to EM",
      <>
        Your account{state.email ? <> (<strong>{state.email}</strong>)</> : null} is
        ready. Sign in with the credentials your HR team shared with you —
        you'll then be guided through a short onboarding to set up your
        profile.
      </>,
      <button
        type="button"
        className="ob-btn ob-btn--primary"
        onClick={() => navigate("/login")}
      >
        Sign in to get started
      </button>,
    );

  if (state.status === "used")
    return card(
      "✅",
      "You're already set up",
      "This onboarding link has already been used. Just sign in as usual.",
      <button
        type="button"
        className="ob-btn ob-btn--primary"
        onClick={() => navigate("/login")}
      >
        Go to sign in
      </button>,
    );

  if (state.status === "expired")
    return card(
      "⌛",
      "This link has expired",
      "Invitation links are valid for 48 hours. Ask your HR team to send you a fresh one.",
    );

  return card(
    "🤔",
    "Invalid link",
    "This invitation link doesn't look right. Double-check the URL or ask your HR team to resend it.",
  );
}
